import { B_G1 } from "../constants.js";
import { Bn254InvalidPointError } from "../errors.js";
import * as Fp from "../Fp/index.js";

/**
 * Create G1 point from affine coordinates with validation
 *
 * @see https://voltaire.tevm.sh/crypto for BN254 cryptography documentation
 * @since 0.0.0
 * @param {bigint} x - X coordinate
 * @param {bigint} y - Y coordinate
 * @returns {import('../G1PointType.js').G1PointType} G1 point
 * @throws {Bn254InvalidPointError} If point not on bn254 G1 curve
 * @example
 * ```javascript
 * import * as G1 from './crypto/bn254/G1/index.js';
 * const point = G1.fromAffine(1n, 2n);
 * ```
 */
export function fromAffine(x, y) {
	const x_mod = Fp.mod(x);
	const y_mod = Fp.mod(y);

	const y2 = Fp.mul(y_mod, y_mod);
	const x3 = Fp.mul(Fp.mul(x_mod, x_mod), x_mod);
	const rhs = Fp.add(x3, B_G1);

	if (y2 !== rhs) {
		throw new Bn254InvalidPointError("Point not on bn254 G1 curve", {
			code: -32602,
			context: { x: x_mod.toString(16), y: y_mod.toString(16), curve: "G1" },
			docsPath: "/crypto/bn254#g1-operations",
		});
	}

	return /** @type {import('../G1PointType.js').G1PointType} */ ({
		x: x_mod,
		y: y_mod,
		z: 1n,
	});
}
